import React, { useEffect } from 'react';
import { Button, Typography } from '@crisellevaldez/speculo-ui';
import {
  signInWithPopup,
  GoogleAuthProvider,
  FacebookAuthProvider,
  TwitterAuthProvider,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import { auth } from '../lib/firebase';
import { useNavigate, useLocation } from 'react-router-dom';
import { clearCachedOrganization, setCachedOrganization, useOrganizationStore } from '../stores/useOrganizationStore';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FormField } from '../components/common/FormField';
import { LogIn, Mail, Lock } from 'lucide-react';
import { validateUser } from '../api/remiApi';
import {
  ACCOUNT_REMOVED_MESSAGE,
  isFirebaseUserDisabled,
  validationRejectionMessage,
} from '../utils/loginErrorCopy';
import { SUPPORT_CONTACT } from '../constants/supportContact';

const loginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email format'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, organization, error: authError, loading } = useOrganizationStore();

  const {
    register,
    handleSubmit,
    setError: setFormError,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const [error, setError] = React.useState<string | null>(null);

  // Get the return URL from location state, default to dashboard
  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname || '/dashboard';

  // Clean up password reset flow state on mount
  useEffect(() => {
    if (sessionStorage.getItem('passwordResetFlow') === 'true') {
      sessionStorage.removeItem('passwordResetFlow');
      clearCachedOrganization();
      auth.signOut();
    }
  }, []);

  // Redirect once authenticated with org loaded
  useEffect(() => {
    if (isAuthenticated && organization && !authError) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, organization, authError, navigate, from]);

  const handleSocialLogin = async (providerType: 'google' | 'facebook' | 'twitter') => {
    try {
      const provider =
        providerType === 'google'
          ? new GoogleAuthProvider()
          : providerType === 'facebook'
            ? new FacebookAuthProvider()
            : new TwitterAuthProvider();

      const result = await signInWithPopup(auth, provider);
      clearCachedOrganization(result.user);

      if (!result.user.email) {
        throw new Error('No email provided from social login');
      }

      const validation = await validateUser();
      if (!validation.valid) {
        await auth.signOut();
        throw new Error(validationRejectionMessage(validation.reason));
      }
      if (validation.user?.organization_id) {
        setCachedOrganization({ id: validation.user.organization_id }, result.user);
        await useOrganizationStore.getState().fetchOrganization();
      }
    } catch (err) {
      const error = err as { code?: string; message?: string };
      console.error(`${providerType} login failed:`, err);

      if (isFirebaseUserDisabled(error)) {
        setError(ACCOUNT_REMOVED_MESSAGE);
      } else if (error.code === 'auth/popup-closed-by-user') {
        setError('Login cancelled. Please try again.');
      } else if (error.code === 'auth/popup-blocked') {
        setError('Login popup was blocked. Please allow popups and try again.');
      } else {
        setError(error.message || 'Authentication failed. Please try again.');
      }

      try {
        await auth.signOut();
      } catch (signOutErr) {
        console.error('Error signing out after failed social login:', signOutErr);
      }
    }
  };

  const onSubmit = async (data: LoginFormData) => {
    try {
      const credential = await signInWithEmailAndPassword(auth, data.email.toLowerCase(), data.password);
      clearCachedOrganization(credential.user);

      const validation = await validateUser();
      if (!validation.valid) {
        await auth.signOut();
        setError(validationRejectionMessage(validation.reason));
        return;
      }
      if (validation.user?.organization_id) {
        setCachedOrganization({ id: validation.user.organization_id }, credential.user);
        await useOrganizationStore.getState().fetchOrganization();
      }
    } catch (err) {
      const error = err as { code?: string; message?: string };
      console.error('Login error:', err);

      if (isFirebaseUserDisabled(error)) {
        setError(ACCOUNT_REMOVED_MESSAGE);
      } else if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        setError('Invalid email or password. Please try again.');
      } else if (error.code === 'auth/user-not-found') {
        setError('Account not found. Please sign up using your invite link.');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please try again later.');
      } else {
        setError(error.message || 'An error occurred. Please try again.');
      }
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-background">
      <div className="flex justify-center mb-8">
        <img src="/speculo.ai-black.png" alt="Speculo AI" className="h-12 dark:hidden" />
        <img src="/speculo.ai-white.png" alt="Speculo AI" className="h-12 hidden dark:block" />
      </div>
      <div className="w-full max-w-md space-y-6 bg-card rounded-lg shadow-xl p-8 mb-6 border border-border">
        <div className="text-center">
          <Typography variant="h1" className="text-3xl font-bold">
            Welcome Back
          </Typography>
          <p className="text-muted-foreground mt-2">Sign in to access your dashboard</p>
        </div>

        {(authError || error) && (
          <div className="p-4 text-sm text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30 rounded-lg" role="alert">
            {authError || error}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 mt-8" noValidate>
          <div className="space-y-4">
            <FormField
              label=""
              type="email"
              error={errors.email?.message}
              registration={register('email')}
              placeholder="Enter your email"
              startIcon={<Mail className="text-muted-foreground w-5 h-5" />}
            />

            <FormField
              label=""
              type="password"
              error={errors.password?.message}
              registration={register('password')}
              placeholder="Enter your password"
              startIcon={<Lock className="text-muted-foreground w-5 h-5" />}
            />
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => {
                  const email = getValues('email');
                  if (email) {
                    navigate('/reset-password', { state: { email } });
                  } else {
                    setFormError('email', {
                      type: 'manual',
                      message: 'Please enter your email first',
                    });
                  }
                }}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Forgot Password?
              </button>
            </div>
          </div>

          <Button
            type="submit"
            variant="primary"
            className="w-full h-9"
            isLoading={isSubmitting}
            disabled={isSubmitting || loading}
          >
            <LogIn className="w-4 h-4 mr-2" />
            Sign In
          </Button>
        </form>

        <div className="relative mt-8">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 text-muted-foreground bg-card">Or continue with</span>
          </div>
        </div>

        <Button
          variant="secondary"
          onClick={() => handleSocialLogin('google')}
          className="w-full h-9"
          disabled={loading || isSubmitting}
        >
          <img
            src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
            alt="Google"
            className="w-5 h-5 mr-2"
          />
          Sign in with Google
        </Button>
      </div>
      <div className="w-full max-w-md p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/30 rounded-lg">
        <div className="space-y-2">
          <p className="font-medium text-amber-800 dark:text-amber-300">
            New to Speculo?
          </p>
          <p className="text-sm text-amber-700 dark:text-amber-400">
            You must use your personal invitation link to create an account. Check your email for an invite, or contact{' '}
            <a
              href={`mailto:${SUPPORT_CONTACT.email}`}
              className="text-amber-800 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-200 underline font-medium"
            >
              {SUPPORT_CONTACT.email}
            </a>{' '}
            to request access.
          </p>
        </div>
      </div>
    </div>
  );
};

export { LoginPage };
export default LoginPage;
